// apps/frontend/src/lib/streak.ts
// Système de streak façon Duolingo : compte les jours consécutifs où
// l'user a ouvert l'app. Reset si gap > 1 jour. Persisté en localStorage.
//
// API :
//   recordVisit() — à appeler une fois par mount de l'app (rate-limité au jour)
//   getStreak() — retourne { current, longest, lastVisitDate }
//   subscribeToStreak(cb) — pour qu'un composant écoute les changements

const STREAK_KEY = 'glowp_streak';

export interface StreakData {
  /** Nombre de jours consécutifs (1 = aujourd'hui seulement) */
  current: number;
  /** Record historique */
  longest: number;
  /** ISO date 'YYYY-MM-DD' de la dernière visite comptée */
  lastVisitDate: string | null;
  /** True si le streak a augmenté lors du dernier recordVisit (1 fois par jour) */
  justIncremented?: boolean;
}

const EMPTY: StreakData = {
  current: 0,
  longest: 0,
  lastVisitDate: null,
};

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function yesterdayIso(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function read(): StreakData {
  try {
    const raw = localStorage.getItem(STREAK_KEY);
    if (!raw) return { ...EMPTY };
    const parsed = JSON.parse(raw);
    return {
      current: typeof parsed.current === 'number' ? parsed.current : 0,
      longest: typeof parsed.longest === 'number' ? parsed.longest : 0,
      lastVisitDate:
        typeof parsed.lastVisitDate === 'string' ? parsed.lastVisitDate : null,
    };
  } catch {
    return { ...EMPTY };
  }
}

function write(data: StreakData): void {
  try {
    localStorage.setItem(
      STREAK_KEY,
      JSON.stringify({
        current: data.current,
        longest: data.longest,
        lastVisitDate: data.lastVisitDate,
      })
    );
  } catch {
    /* localStorage indispo */
  }
}

// Pub/sub minimal pour que les composants se mettent à jour quand le streak
// change (typiquement au passage de jour).
type Listener = (data: StreakData) => void;
const listeners = new Set<Listener>();

function notify(data: StreakData): void {
  for (const l of listeners) {
    try {
      l(data);
    } catch {
      /* swallow */
    }
  }
}

export function subscribeToStreak(cb: Listener): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/**
 * Enregistre une visite "aujourd'hui". Idempotent au sein de la même journée
 * (deux appels le même jour = un seul incrément). Si le dernier visit était
 * hier → streak++. Si > 1 jour → streak reset à 1.
 *
 * Retourne le nouvel état avec `justIncremented` à true si l'incrément
 * vient d'avoir lieu (utile pour célébrer côté UI).
 */
export function recordVisit(): StreakData {
  const today = todayIso();
  const data = read();

  if (data.lastVisitDate === today) {
    // Déjà compté aujourd'hui, no-op silencieux
    return { ...data, justIncremented: false };
  }

  let nextCurrent: number;
  if (data.lastVisitDate === yesterdayIso()) {
    nextCurrent = data.current + 1;
  } else {
    // Premier jour OU gap → streak repart à 1
    nextCurrent = 1;
  }
  const nextLongest = Math.max(data.longest, nextCurrent);

  const next: StreakData = {
    current: nextCurrent,
    longest: nextLongest,
    lastVisitDate: today,
    justIncremented: true,
  };

  write(next);
  notify(next);
  return next;
}

export function getStreak(): StreakData {
  return read();
}

export function resetStreak(): void {
  write(EMPTY);
  notify(EMPTY);
}
